import { useCallback, useEffect, useRef, useState } from "react";
import axios from "../../../api/axiosInstance";
import useIsMobile from "../../../hooks/useIsMobile";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { canCreateCatalog } from "../../utils/catalogEditPermissions";
import { buildSearchBlob, formatItemsFamiliesDate, readItemsFamiliesUser } from "../itemsFamiliesUi";
import { catalogMessages } from "./catalogApprovalMessages";
import { CatalogApprovalPayloadValue } from "./CatalogCreationApprovalInbox";
import CatalogApprovalChangePreview from "./CatalogApprovalChangePreview";
import CatalogApprovalRequestEditor from "./CatalogApprovalRequestEditor";
import { useCatalogPendingText } from "./catalogPendingMessages";
import "./PendingCatalogRequests.css";

export function usePendingCatalogRequests({ entityType, businessUnitId, enabled, refreshKey }) {
  const user = readItemsFamiliesUser();
  const scope = JSON.stringify([businessUnitId, entityType, user?.id, user?.permissions]);
  const sequence = useRef(0);
  const [result, setResult] = useState({ scope: null, requests: [], loading: false, error: false });
  const refresh = useCallback(async () => {
    const requestNumber = ++sequence.current;
    if (!enabled || !businessUnitId) return;
    setResult({ scope, requests: [], loading: true, error: false });
    try {
      const response = await axios.get("/api/purchasing/catalog-creation-approvals/requests", {
        params: { business_unit_id: businessUnitId, entity_type: entityType, status: "pending", operation: "create" },
        headers: { "X-Pilcore-Business-Unit-Id": String(businessUnitId) },
      });
      if (requestNumber !== sequence.current) return;
      const requests = (Array.isArray(response.data?.requests) ? response.data.requests : []).filter((request) =>
        Number(request.business_unit_id) === Number(businessUnitId) && request.entity_type === entityType &&
        request.status === "pending" && request.operation === "create");
      setResult({ scope, requests, loading: false, error: false });
    } catch {
      if (requestNumber === sequence.current) setResult({ scope, requests: [], loading: false, error: true });
    }
  }, [businessUnitId, enabled, entityType, scope]);
  useEffect(() => {
    refresh();
    return () => { sequence.current += 1; };
  }, [refresh, refreshKey]);
  const current = enabled && result.scope === scope;
  return { requests: current ? result.requests : [], loading: Boolean(enabled && businessUnitId && (!current || result.loading)),
    error: current && result.error, refresh };
}

export function CatalogPublicationFilter({ value, onChange }) {
  const text = useCatalogPendingText();
  return <div className="ifm-statusFilters" role="group" aria-label={text.status}>
    {["published", "pending"].map((status) => <button type="button" key={status}
      className={`spx-btn ${value === status ? "ifm-statusFilter--active" : ""}`}
      aria-pressed={value === status} onClick={() => onChange(status)}>{text[status]}</button>)}
  </div>;
}

export function pendingClassificationName(request, level) {
  return request.classification?.[level]?.name || request.payload_labels?.[`${level}_id`] || "";
}

const searchableValues = (value) => value && typeof value === "object"
  ? Object.values(value).flatMap(searchableValues) : [value];

export default function PendingCatalogRequests({ entityType, businessUnitId, state, search = "", familyFilter = "all", categoryFilter = "all" }) {
  const text = useCatalogPendingText();
  const { languageId } = usePmsTheme();
  const t = catalogMessages(languageId);
  const mobile = useIsMobile();
  const currentUser = readItemsFamiliesUser();
  const [editing, setEditing] = useState(null);
  const [saved, setSaved] = useState(false);
  const scope = `${businessUnitId}:${entityType}:${currentUser?.id}`;
  useEffect(() => { setEditing(null); setSaved(false); }, [scope]);
  const query = search.trim().toLowerCase();
  const requests = state.requests.filter((request) =>
    (familyFilter === "all" || pendingClassificationName(request, "family") === familyFilter) &&
    (categoryFilter === "all" || pendingClassificationName(request, "category") === categoryFilter) &&
    (!query || buildSearchBlob(searchableValues([request.payload, request.payload_labels, request.classification, request.requester?.name, request.requester_name])).includes(query)));
  const mayEdit = (request) => request.can_edit === true && canCreateCatalog(currentUser, entityType);
  const details = (request) => <details><summary>{text.details}</summary>
    <CatalogApprovalChangePreview request={request} renderValue={(value, labels) => <CatalogApprovalPayloadValue value={value} labels={labels} t={t} />} />
  </details>;
  const edit = (request) => mayEdit(request) ? <button type="button" className="spx-btn spx-btn--ghost"
    aria-label={`${text.edit}: ${request.payload?.name || request.payload?.code || `#${request.id}`}`}
    onClick={() => setEditing({ id: request.id, scope })}>{text.edit}</button> : null;
  const classification = (request) => ["family", "subfamily", "category", "subcategory"].map((level) => pendingClassificationName(request, level)).filter(Boolean).join(" › ");
  return <div className="catalogPendingRequests">
    <div className="spx-actions"><p className="spx-note">{text.hint}</p>
      <button type="button" className="spx-btn" disabled={state.loading || !businessUnitId} onClick={() => { setSaved(false); state.refresh(); }}>{text.refresh}</button>
    </div>
    {saved && <div className="spx-banner spx-banner--success" role="status">{text.saved}</div>}
    {!businessUnitId ? <div className="spx-empty">{text.missingScope}</div> : state.loading ? <div className="spx-empty" role="status">{text.loading}</div> : state.error ?
      <div className="spx-banner spx-banner--error" role="alert">{text.error}</div> : <>
        <p className="spx-note" aria-live="polite">{text.count}: <strong>{requests.length}</strong></p>
        {!requests.length ? <div className="spx-empty">{text.empty}</div> : mobile ? <div className="spx-mobileList">
          {requests.map((request) => <article className="spx-mobileCard" key={request.id}>
            <div className="spx-code">{request.payload?.code || "—"}</div><strong>{request.payload?.name || "—"}</strong>
            <p className="ifm-tag">{text.pending} · #{request.id}</p>
            {classification(request) && <p>{classification(request)}</p>}
            <p>{request.payload?.description}</p><p>{text.requestedBy}: {request.requester?.name || request.requester_name || "—"}</p>
            <p>{text.requestedAt}: {formatItemsFamiliesDate(request.created_at)}</p>{details(request)}{edit(request)}
          </article>)}
        </div> : <div className="spx-tableWrap"><table className="spx-table ifm-table--compact">
          <thead><tr>{[text.code, text.name, text.classification, text.status, text.requestedBy, text.requestedAt, text.action].map((title) => <th key={title}>{title}</th>)}</tr></thead>
          <tbody>{requests.map((request) => <tr key={request.id}>
            <td><span className="spx-code">{request.payload?.code || "—"}</span></td>
            <td><strong>{request.payload?.name || "—"}</strong><p>{request.payload?.description}</p>{details(request)}</td>
            <td>{classification(request) || "—"}</td><td><span className="ifm-tag">{text.pending} · #{request.id}</span></td>
            <td>{request.requester?.name || request.requester_name || "—"}</td><td>{formatItemsFamiliesDate(request.created_at)}</td><td>{edit(request)}</td>
          </tr>)}</tbody>
        </table></div>}
      </>}
    {editing?.scope === scope && state.requests.some((request) => request.id === editing.id && mayEdit(request)) &&
      <CatalogApprovalRequestEditor requestId={editing.id} businessUnitId={businessUnitId} onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); setSaved(true); state.refresh(); }} />}
  </div>;
}
